import { ModalController } from '@ionic/angular/standalone';
import { Injectable, inject } from '@angular/core';
import { Subscription, takeUntil } from 'rxjs';

import {
  NotificationEvent,
  CurrentCall,
  isPopupNotificationEvent,
  isCallEndNotificationEvent,
  isCallStartNotificationEvent,
} from '@app/interfaces/sse-notification.interface';
import { NotificationPopupComponent } from '@app/components/notification-popup/notification-popup.component';
import { CallPopupComponent } from '@app/components/call-popup/call-popup.component';
import { TitleNotificationService } from '@app/services/title-notification.service';
import { ActiveCallsService } from '@app/services/active-calls.service';
import { UserService } from '@app/services/user.service';
import { SseService } from '@app/services/sse.service';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class SseNotificationService {
  private sseSrv = inject(SseService);
  private userSrv = inject(UserService);
  private modalCtrl = inject(ModalController);
  private activeCallsSrv = inject(ActiveCallsService);
  private titleNotificationSrv = inject(TitleNotificationService);

  private url = `${environment.restAPI}/notifications/subscribe`;
  private subscription?: Subscription;
  private openCallModals = new Map<string, HTMLIonModalElement>();

  connect(): void {
    this.disconnect();

    const token = this.userSrv.accessToken;
    if (!token) {
      return;
    }

    this.subscription = this.sseSrv
      .getServerSentEvent(`${this.url}?token=${token}`)
      .pipe(takeUntil(this.userSrv.logout$))
      .subscribe({
        next: (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data) as NotificationEvent;
            if (isPopupNotificationEvent(data)) {
              this.titleNotificationSrv.startBlink();
              this.showPopup(data.html, data.title);
            } else if (isCallStartNotificationEvent(data)) {
              this.handleCallStart(data.call);
            } else if (isCallEndNotificationEvent(data)) {
              this.handleCallEnd(data.call.UUID);
            }
          } catch (err) {
            console.error('[SSE] Failed to parse notification message', err);
          }
        },
        error: (err) => console.error('[SSE] Connection error', err),
      });
  }

  disconnect(): void {
    this.subscription?.unsubscribe();
    this.openCallModals.forEach((modal) => modal.dismiss());
    this.openCallModals.clear();
    this.activeCallsSrv.calls.forEach((call) => this.activeCallsSrv.removeCall(call.UUID));
  }

  private handleCallStart(call: CurrentCall): void {
    this.activeCallsSrv.upsertCall(call);
    if (call.POPUP && !this.activeCallsSrv.isPopupDismissed(call.UUID) && !this.openCallModals.has(call.UUID)) {
      this.showCallPopup(call.UUID);
    }
  }

  private handleCallEnd(uuid: string): void {
    this.activeCallsSrv.removeCall(uuid);
    const modal = this.openCallModals.get(uuid);
    if (modal) {
      this.openCallModals.delete(uuid);
      modal.dismiss();
    }
  }

  private async showCallPopup(uuid: string): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: CallPopupComponent,
      componentProps: { uuid },
      backdropDismiss: false,
      cssClass: 'notification-popup-modal',
    });
    this.openCallModals.set(uuid, modal);
    await modal.present();
    await modal.onDidDismiss();
    if (this.openCallModals.get(uuid) === modal) {
      this.openCallModals.delete(uuid);
    }
    this.activeCallsSrv.markPopupDismissed(uuid);
  }

  private async showPopup(html: string, title?: string): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: NotificationPopupComponent,
      componentProps: { title, html },
      backdropDismiss: false,
      cssClass: 'notification-popup-modal',
    });
    await modal.present();
    await modal.onDidDismiss();
    this.titleNotificationSrv.stopBlink();
  }
}
